import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { parseApiError } from '@/shared/api';
import { Button } from './Button';

interface ErrorStateProps {
  error: unknown;
  /** Per-status message overrides, e.g. `{ 404: 'Subscription not found.' }`. */
  messages?: Partial<Record<number, string>>;
  onRetry?: () => void;
}

/** `p.error-state[role=alert]` with the server message and an optional Retry link. */
export function ErrorState({ error, messages, onRetry }: ErrorStateProps) {
  const { message, traceId } = parseApiError(error, messages);
  return (
    <p className="error-state" role="alert">
      {message}
      {traceId && <span className="subtle"> (Reference: {traceId})</span>}
      {onRetry && (
        <>
          {' '}
          <Button variant="link" onClick={onRetry}>
            Retry
          </Button>
        </>
      )}
    </p>
  );
}

interface ErrorScreenProps {
  title: string;
  error: unknown;
  onRetry?: () => void;
  /** Further actions after Retry, e.g. "Sign in again". */
  actions?: ReactNode;
}

/**
 * `section.card.error-screen[role=alert]`: replaces a page's content when the data it cannot do
 * without failed to load. Shows the reason, the HTTP status and the server reference.
 */
export function ErrorScreen({ title, error, onRetry, actions }: ErrorScreenProps) {
  const { message, status, traceId } = parseApiError(error);
  const details = [status !== null && `HTTP ${status}`, traceId && `Reference: ${traceId}`].filter(
    Boolean,
  );
  return (
    <section className="card error-screen" role="alert">
      <div className="card__titles">
        <h2 className="card__title">{title}</h2>
        <p className="error-screen__message">{message}</p>
        {details.length > 0 && <p className="card__sub">{details.join(' · ')}</p>}
      </div>
      {(Boolean(onRetry) || Boolean(actions)) && (
        <div className="error-screen__actions">
          {onRetry && (
            <Button variant="primary" onClick={onRetry}>
              Retry
            </Button>
          )}
          {actions}
        </div>
      )}
    </section>
  );
}

export function NotFoundState({
  title = 'Page not found',
  children,
}: {
  title?: string;
  children?: ReactNode;
}) {
  return (
    <section className="card">
      <div className="card__titles">
        <h2 className="card__title">{title}</h2>
        <p className="card__sub">{children ?? 'The page you requested does not exist.'}</p>
      </div>
      <p>
        <Link to="/overview">Go to the overview</Link>
      </p>
    </section>
  );
}
